// The one cache of the API's answers (TanStack Query, ADR 0055). `useApi`
// reads through it, so a page that comes back (Back from a work's series or
// contributor page to the Library under it) shows what it showed at once and
// refreshes behind it, instead of starting empty. In memory only; forgotten
// when the session ends, and, for pages no longer on screen, whenever a
// write changes something (`changes.ts`).
//
// It imports only TanStack, so `src/setupTests.ts` can forget between tests
// without loading the API client before a test mocks it.
import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Kept until forgotten (below), not for TanStack's five minutes.
      gcTime: Infinity,
      // A page asks again when it mounts or reloads, and only then: as
      // `useApi` did before TanStack, so no page refetches on its own.
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // A page whose data comes back equal still gets a new object, as
      // before, so effects keyed on it run as they did.
      structuralSharing: false,
      // Asks even when the browser says it's offline: the service worker
      // may answer from its stored copy (ADR 0005).
      networkMode: "offlineFirst",
    },
  },
});

const cache = queryClient.getQueryCache();
const MAX_REMEMBERED = 50;

// The 50 most recent answers, at most: past that, the one no page shows
// that was stored longest ago goes.
cache.subscribe((event) => {
  if (event.type !== "added" || cache.getAll().length <= MAX_REMEMBERED) {
    return;
  }
  const [oldest] = cache
    .getAll()
    // The one just added has no page yet, but is about to.
    .filter((query) => query !== event.query && !query.isActive())
    .sort((a, b) => a.state.dataUpdatedAt - b.state.dataUpdatedAt);
  if (oldest) cache.remove(oldest);
});

/** Forget every answer (when a session ends, and between tests). */
export const forgetApiResponses = (): void => queryClient.clear();
